import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, inr, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, PencilSimple, Trash, ArrowsClockwise, CheckCircle, Printer, EnvelopeSimple, WhatsappLogo, MagnifyingGlass, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import QRCodeButton from "@/components/QRCodeButton";
import PageExportBar from "@/components/PageExportBar";
import { openPrintStatement } from "@/lib/printStatement";

export default function Quotations() {
  const { activeCompanyId, activeCompany, user, can, activeStoreId } = useAuth();
  const canApprove = user && ["admin", "manager", "regional_manager"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(null);
  const [approving, setApproving] = useState(null);

  // Filters
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [status, setStatus] = useState("all");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/quotations"); setRows(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId]);

  const del = async (id) => {
    if (!confirm("Delete this quotation?")) return;
    try { await api.delete(`/quotations/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const convert = async (id) => {
    if (!confirm("Convert this approved quotation into a Sales Invoice? Stock will be adjusted and a new invoice number will be issued.")) return;
    setConverting(id);
    try {
      const { data } = await api.post(`/quotations/${id}/convert`);
      toast.success(`Invoice ${data.number} created`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setConverting(null); }
  };

  const approve = async (id) => {
    if (!confirm("Approve this quotation? Once approved, Admin/Manager can convert it into a Sales Invoice.")) return;
    setApproving(id);
    try {
      await api.post(`/quotations/${id}/approve`);
      toast.success("Quotation approved");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setApproving(null); }
  };

  const filtered = useMemo(() => {
    const qn = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (start && r.date < start) return false;
      if (end && r.date > end) return false;
      if (status !== "all" && r.status !== status) return false;
      if (qn) {
        const cust = (r.customer_name || "").toLowerCase();
        const num = (r.number || "").toLowerCase();
        if (!cust.includes(qn) && !num.includes(qn)) return false;
      }
      return true;
    });
  }, [rows, q, start, end, status]);

  const totals = useMemo(() => {
    const grand = filtered.reduce((s, r) => s + Number(r.grand_total || 0), 0);
    return { count: filtered.length, grand };
  }, [filtered]);

  const resetFilters = () => { setQ(""); setStart(""); setEnd(""); setStatus("all"); };
  const hasActiveFilters = q || start || end || status !== "all";

  const exportColumns = [
    { key: "number", label: "Number" },
    { key: "date", label: "Date" },
    { key: "customer_name", label: "Customer" },
    { key: "status", label: "Status" },
    { key: "grand_total", label: "Grand Total" },
  ];

  const printStatement = () => {
    if (!filtered.length) { toast.error("No quotations in the current filter."); return; }
    const groups = {};
    filtered.forEach((r) => { (groups[r.status] = groups[r.status] || []).push(r); });
    const label = { draft: "Drafts", approved: "Approved", converted: "Converted to Invoice", rejected: "Rejected", sent: "Sent" };
    const cols = [
      { key: "number", label: "Quotation #", mono: true },
      { key: "date", label: "Date", mono: true },
      { key: "customer", label: "Customer" },
      { key: "amount", label: "Grand Total", align: "right", mono: true },
    ];
    const sections = Object.keys(groups).map((k) => {
      const list = groups[k];
      const sub = list.reduce((s, r) => s + Number(r.grand_total || 0), 0);
      return {
        heading: label[k] || k.toUpperCase(),
        subheading: `${list.length} quotation(s) · Total <b>${inr(sub)}</b>`,
        columns: cols,
        rows: list.map((r) => ({
          number: r.number,
          date: r.date,
          customer: r.customer_name || "—",
          amount: inr(r.grand_total),
        })),
      };
    });
    const range = start || end ? `${start || "…"} → ${end || "…"}` : "All dates";
    const res = openPrintStatement({
      title: `Quotations Statement (${range})`,
      company: activeCompany || {},
      sections,
      totals: [
        { label: "Quotation Count", value: String(totals.count) },
        { label: "Grand Total", value: inr(totals.grand), tone: "pos" },
      ],
      note: hasActiveFilters ? `Filters applied — ${totals.count} quotation(s) shown.` : "Full quotation list.",
    });
    if (!res.ok) toast.error(res.error);
    else toast.success(`Preparing statement for ${totals.count} quotation(s)…`);
  };

  return (
    <div className="page-shell space-y-6" id="report-print-root">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Sales</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Quotations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {totals.count} of {rows.length} quotation(s) · Total <b>{inr(totals.grand)}</b>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" className="h-9" onClick={printStatement} data-testid="quotations-print-statement">
            <Printer size={16} className="mr-1.5" /> Print Statement
          </Button>
          <PageExportBar
            title="Quotations"
            filename={`quotations-${new Date().toISOString().slice(0,10)}`}
            columns={exportColumns}
            rows={filtered}
            testidPrefix="quotations"
          />
          <Link to="/quotations/new">
            <Button className="h-9" data-testid="new-quotation-btn">
              <Plus size={15} weight="bold" className="mr-1.5" /> New Quotation
            </Button>
          </Link>
        </div>
      </div>

      <div className="card-flat p-3 flex flex-wrap items-end gap-3 no-print">
        <div className="space-y-1.5">
          <Label className="eyebrow">From Date</Label>
          <Input type="date" min="1900-01-01" value={start} onChange={(e) => setStart(e.target.value)}
            className="h-9 font-mono w-40" data-testid="quot-filter-start" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">To Date</Label>
          <Input type="date" min="1900-01-01" value={end} onChange={(e) => setEnd(e.target.value)}
            className="h-9 font-mono w-40" data-testid="quot-filter-end" />
        </div>
        <div className="space-y-1.5 flex-1 min-w-[220px]">
          <Label className="eyebrow">Search</Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Quotation # or customer name"
              className="pl-9 h-9" data-testid="quot-filter-q" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Status</Label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="quot-filter-status">
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="converted">Converted</option>
          </select>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" className="h-9" onClick={resetFilters} data-testid="quot-filter-reset">
            <X size={14} className="mr-1.5" /> Clear
          </Button>
        )}
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Status</th>
              <th className="num">Grand Total</th>
              <th className="num" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">
                {rows.length === 0 ? "No quotations yet." : "No quotations match the current filter."}
              </td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} data-testid={`quot-row-${r.id}`}>
                <td className="font-mono text-[12.5px]">
                  <Link to={`/quotations/${r.id}`} className="text-primary hover:underline">{r.number}</Link>
                </td>
                <td>{fmtDate(r.date)}</td>
                <td>{r.customer_name}</td>
                <td>
                  {r.status === "converted" && r.converted_invoice_id ? (
                    <Link to={`/sales/${r.converted_invoice_id}`}
                      className="pill pill-success hover:underline"
                      data-testid={`quot-converted-${r.id}`}
                      title="Open the generated Sales Invoice">
                      Converted → {r.converted_invoice_number || "Invoice"}
                    </Link>
                  ) : (
                    <span className={`pill ${r.status === "approved" ? "pill-success" : r.status === "rejected" ? "pill-danger" : "pill-info"}`}>{r.status}</span>
                  )}
                </td>
                <td className="num font-semibold">{inr(r.grand_total)}</td>
                <td className="num" style={{ textAlign: "right" }}>
                  {r.share_token && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="Print quotation"
                        onClick={() => {
                          const w = window.open(
                            `/p/quotation/${r.id}?t=${r.share_token}&autoprint=1`,
                            "_blank",
                            "noopener,noreferrer"
                          );
                          if (!w) toast.error("Please allow pop-ups to print");
                        }}
                        data-testid={`quot-print-${r.id}`}
                      >
                        <Printer size={14} />
                      </Button>
                      <QRCodeButton
                        url={`${window.location.origin}/p/quotation/${r.id}?t=${r.share_token}`}
                        label={`QR · ${r.number}`}
                        testid={`quot-qr-${r.id}`}
                      />
                      <Button variant="ghost" size="icon" className="h-8 w-8"
                        style={{ color: r.customer_phone ? "#128C7E" : "#94a3b8" }}
                        title={r.customer_phone
                          ? `WhatsApp quotation → ${r.customer_phone}`
                          : "No mobile on file for this customer"}
                        onClick={() => {
                          const phone = String(r.customer_phone || "").replace(/[^0-9]/g, "");
                          if (!phone) { toast.error("Customer has no mobile number. Add one in Customers first."); return; }
                          const waPhone = phone.length === 10 ? `91${phone}` : phone;
                          const url = `${window.location.origin}/p/quotation/${r.id}?t=${r.share_token}`;
                          const compName = activeCompany?.name || "Vetrich";
                          const msg = `Hi ${r.customer_name}, please review your quotation *${r.number}* dated ${r.date} for *${inr(r.grand_total)}*.\n\nView + accept online:\n${url}\n\n— ${compName}`;
                          window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
                        }}
                        data-testid={`quot-whatsapp-${r.id}`}>
                        <WhatsappLogo size={14} weight="fill" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8"
                        title="Email quotation"
                        onClick={async () => {
                          try {
                            await api.post("/email/quotation", { quotation_id: r.id });
                            toast.success(`Quotation ${r.number} emailed`);
                          } catch (err) { toast.error(formatApiError(err)); }
                        }}
                        data-testid={`quot-email-${r.id}`}>
                        <EnvelopeSimple size={14} />
                      </Button>
                    </>
                  )}
                  {r.status !== "converted" && r.status !== "approved" && canApprove && (
                    <Button variant="ghost" size="icon" className="h-8 w-8"
                      style={{ color: "hsl(var(--success))" }}
                      title="Approve quotation (Admin/Manager only)"
                      onClick={() => approve(r.id)}
                      disabled={approving === r.id}
                      data-testid={`quot-approve-${r.id}`}>
                      <CheckCircle size={14} weight="fill" />
                    </Button>
                  )}
                  {r.status === "approved" && canApprove && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-primary"
                      title="Convert to Sales Invoice"
                      onClick={() => convert(r.id)}
                      disabled={converting === r.id}
                      data-testid={`quot-convert-${r.id}`}>
                      <ArrowsClockwise size={14} />
                    </Button>
                  )}
                  <Link to={`/quotations/${r.id}`}>
                    <Button variant="ghost" size="icon" className="h-8 w-8"><PencilSimple size={14} /></Button>
                  </Link>
                  {can("quotations", "delete") && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(r.id)} data-testid={`quot-del-${r.id}`}>
                      <Trash size={14} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
